<script setup lang="ts">
import ModalRoot from "@/components/Reusables/Modal/ModalRoot.vue";
import { useEnum } from "@/stores/project/project.enum";
import { useProject } from "@/stores/project/project.store";
import { useFrontegg, useFronteggAuthGuard } from "@frontegg/vue";
import { computed, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import FronteggClient from "./api/FronteggClient";
import ApolloClientProvider from "./graphql/providers/ApolloClientProvider.vue";
import { useAuthStateStore } from "./stores/Utils/auth/AuthState";
import { organizationsStore } from "./stores/Utils/auth/Organizations";
import { isSwitchingTenant } from "./state/tenantSwitch";
import BaseView from "./views/BaseView.vue";
import { useWelcomeFlow, WelcomeTour, getWelcomeDeck } from "@features/WelcomeFlow";
import { useWelcomeFlowStore } from "@/stores/welcomeFlow/welcomeFlow.store";
import { checkAndShowPlatformReview, PlatformReviewCard } from "@features/PlatformReview";
import { usePlatformReviewStore } from "@features/PlatformReview/platformReview.store";

import { storeToRefs } from "pinia";
import "./FlowGuide.css";
import LoadingLogoSpinner from "./components/Reusables/Loading/LoadingLogoSpinner.vue";

const router = useRouter();

// Where a deep link gets parked while the user is sent to log in (see the authState watch below).
const POST_LOGIN_REDIRECT_KEY = "gaiup-post-login-redirect";

const { authState, fronteggAuth: frontegg } = useFrontegg();
useFronteggAuthGuard(); // auto redirects the user to the login page / application
const tenantsLoaded = ref(false);
const authStateStore = useAuthStateStore();
const { isAuthenticated, user } = storeToRefs(authStateStore);
const orgStore = organizationsStore();
const enumStore = useEnum();
const projectStore = useProject();
const { checkAndShow, onTourFinished } = useWelcomeFlow();
const { activeTour, isOnDemand } = storeToRefs(useWelcomeFlowStore());
const activeDeck = computed(() => (activeTour.value ? getWelcomeDeck(activeTour.value.deck) : null));
const { isOpen: isReviewOpen } = storeToRefs(usePlatformReviewStore());

onMounted(() => {});

const hasFetched = ref(false);

watch([isAuthenticated, user, router.currentRoute], async ([newIsAuthenticated, newUser, newCurrentRoute]) => {
  if (newIsAuthenticated && newUser?.accessToken) {
    // During a tenant switch, the dropdown already set orgStore.currentTenant
    // optimistically to the target. Reading user?.tenantId here would still see
    // the OLD tenantId until Frontegg finishes, so reconciling now would flash
    // the previous org name back. Skip; the next time auth state actually
    // changes (post-switch) this watch will reconcile to the truth.
    if (isSwitchingTenant.value) return;

    // A deep link saved before the redirect to login; consumed once, then dropped.
    try {
      const postLoginRedirect = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
      if (postLoginRedirect) {
        sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
        if (postLoginRedirect !== newCurrentRoute.fullPath) router.push(postLoginRedirect);
      }
    } catch {
      // intentional: a blocked/full store just means nothing to restore
    }

    try {
      // This can be fetched from user.tenants - we don't need to call this endpoint
      const tenants = await FronteggClient.get("/identity/resources/users/v2/me/tenants");
      orgStore.setTenants(tenants.data);

      const currentOrg = tenants.data.find((t: any) => t.tenantId === user.value?.tenantId);
      orgStore.setCurrentTenant(currentOrg);

      if (!hasFetched.value && newCurrentRoute.path !== "/") {
        await Promise.all([
          enumStore.fetchBuildingProjectLookups(),
          enumStore.fetchLifeCycleStages(),
          enumStore.fetchDeclaredUnits(),
          enumStore.fetchIndicatorTypeNames(),
          enumStore.fetchMassUnits(),
          enumStore.fetchIndicatorTypes(),
          enumStore.fetchDataSources(),
          enumStore.fetchMaterialTenants(),
          enumStore.fetchCanMapDictionaries(),
          projectStore.fetchLifetimeTable(),
          enumStore.fetchSpecificationForms(),
          enumStore.fetcTagsTypesForms(),
          enumStore.fetchProductTypes(),
        ]);
        hasFetched.value = true;
      }

      tenantsLoaded.value = true;
      void checkAndShow();
      void checkAndShowPlatformReview();
    } catch (err) {
      console.error("err", err);
    }
  }
});

watch(authState, (newAuthState) => {
  if (!newAuthState.isAuthenticated && !newAuthState.isLoading) {
    // Best-effort: a blocked/full store just means no deep link to restore, never a reason to
    // skip the redirect below — useFronteggAuthGuard()'s own guard can't be relied on alone here.
    try {
      if (!frontegg.isAuthRoutes(frontegg.getCurrentRoute())) {
        sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, frontegg.getCurrentRoute());
      }
    } catch {
      // intentional: see comment above
    }

    router.push("/account/login");
  }

  authStateStore.setAuthState(newAuthState);
});
</script>

<template>
  <div id="app">
    <!-- Loading -->
    <template v-if="isAuthenticated">
      <ApolloClientProvider>
        <BaseView />
        <ModalRoot />
        <WelcomeTour
          v-if="activeTour && activeDeck"
          :deck="activeDeck"
          :variant="activeTour.variant"
          :dismissible="isOnDemand"
          :after-creation="activeTour.afterCreation"
          :on-demand="isOnDemand"
          @finished="onTourFinished" />
        <PlatformReviewCard v-if="isReviewOpen" />
      </ApolloClientProvider>
    </template>
    <template v-else>
      <div class="flex h-screen flex-col items-center justify-center">
        <LoadingLogoSpinner />
        <span class="mt-4">Connecting...</span>
      </div>
    </template>
  </div>
</template>
